import axios from 'axios';
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom';
import {
    UserIcon,
    MagnifyingGlassIcon,
    MapPinIcon,
    PhoneIcon,
    UserGroupIcon,
    CheckBadgeIcon,
    ChevronRightIcon,
    CalendarIcon,
    ClockIcon,
    ChevronDownIcon,
    PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { jobStatusLabels } from 'utils/constants';
import moment from 'moment';
import classNames from 'classnames';
import ReviewModal from '../MyOrders/ReviewModal';
import IconReview from '../MyOrders/img/review.png';
import Completed from 'components/Base/Completed';

const PatientsManage = () => {
    const [patients, setPatients] = useState([]);
    const [activeTab, setActiveTab] = useState('patients'); // 'patients' or 'jobs'
    const [jobs, setJobs] = useState([]);
    const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', or 'active-jobs'
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [jobStatusFilter, setJobStatusFilter] = useState('all');
    const [patientsSearch, setPatientsSearch] = useState('');
    const [jobsSearch, setJobsSearch] = useState('');
    const [modalJob, setModalJob] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewJob, setReviewJob] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const sortPatients = (patientsToSort) => {
        return [...patientsToSort].sort((a, b) => {
            const aHasJob = !!a.workingJob;
            const bHasJob = !!b.workingJob;

            if (aHasJob && !bHasJob) return -1;
            if (!aHasJob && bHasJob) return 1;
            return 0;
        });
    };

    const currentLabel = useMemo(() => {
        return jobStatusLabels[jobStatusFilter] || 'Jobs';
    }, [jobStatusFilter]);


    useEffect(() => {
        // Fetch patients
        setIsLoading(true);
        axios.get('/patients')
            .then(({ data }) => {
                setPatients(sortPatients(data.patients));
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error fetching patients:', error);
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        if (activeTab === 'jobs') {
            setIsLoading(true);
            axios.get('/jobs/my')
                .then(({ data }) => {
                    // Add executor to each job
                    for (const job of data) {
                        job.executor = job.bids.find(
                            (bid) => bid.status === 'hired'
                        )?.provider;
                    }
                    setJobs(data);
                    setIsLoading(false);
                })
                .catch(error => {
                    console.error('Error fetching jobs:', error);
                    setIsLoading(false);
                });
        }
    }, [activeTab])

    useEffect(() => {
        setPatients(prev => sortPatients(prev));
    }, [sortBy, jobs]);

    // Add job management functions
    const sendCompleteJob = useCallback(() => {
        axios.patch(`/jobs/${modalJob._id}/complete`)
            .then(() => {
                setCompleted(true);
                setShowModal(false);
                // Refresh jobs
                if (activeTab === 'jobs') {
                    axios.get('/jobs/my')
                        .then(({ data }) => {
                            setJobs(data);
                        })
                        .catch(error => {
                            console.error('Error fetching jobs:', error);
                        });
                }
            })
            .catch((error) => {
                console.error("Error completing job:", error);
            });
    }, [modalJob, activeTab]);

    const disputeJob = useCallback(() => {
        axios.patch(`/jobs/${modalJob._id}/dispute`)
            .then(() => {
                setCompleted(true);
                setShowModal(false);
                // Refresh jobs
                if (activeTab === 'jobs') {
                    axios.get('/jobs/my')
                        .then(({ data }) => {
                            setJobs(data);
                        })
                        .catch(error => {
                            console.error('Error fetching jobs:', error);
                        });
                }
            })
            .catch((error) => {
                console.error("Error disputing job:", error);
            });
    }, [modalJob, activeTab]);

    const cancelJob = useCallback(() => {
        axios.patch(`/jobs/${modalJob._id}/cancel`)
            .then(() => {
                setCompleted(true);
                setShowModal(false);
                // Refresh jobs
                if (activeTab === 'jobs') {
                    axios.get('/jobs/my')
                        .then(({ data }) => {
                            setJobs(data);
                        })
                        .catch(error => {
                            console.error('Error fetching jobs:', error);
                        });
                }
            })
            .catch((error) => {
                console.error("Error canceling job:", error);
            });
    }, [modalJob, activeTab]);

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50">
            {/* Header Section */}
            <div className="mx-auto pt-8" style={{ maxWidth: '1100px' }}>
                <div className="sm:flex sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                            Patient Management
                        </h1>
                        <p className="mt-2 text-sm text-gray-600">
                            Manage and monitor your patients' care journey
                        </p>
                    </div>
                    <div className="mt-4 sm:mt-0 flex items-center gap-4">

                        {activeTab === 'jobs' && (
                            <div className="relative">
                                <button
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    className="inline-flex items-center justify-between w-56 px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                                >
                                    <span>{currentLabel}</span>
                                    <ChevronDownIcon className="h-4 w-4 ml-2" />
                                </button>


                                {isDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                        <div className="py-1" role="menu" aria-orientation="vertical">
                                            {[
                                                { value: 'all', label: 'All Jobs' },
                                                { value: 'active', label: 'Active Jobs' },
                                                { value: 'working', label: 'Working Jobs' },
                                                { value: 'completed', label: 'Completed Jobs' },
                                                { value: 'archive', label: 'Archived Jobs' },
                                                { value: 'dispute', label: 'Jobs in Dispute' },
                                                { value: 'canceled', label: 'Canceled Jobs' },
                                                { value: 'pending', label: 'Pending Jobs' },
                                                { value: 'pending_acceptance', label: 'Pending Acceptance' },
                                                { value: 'pending_reschedule', label: 'Pending Reschedule' },
                                                { value: 'late_canceled', label: 'Late Canceled Jobs' },
                                            ].map(option => (
                                                <button
                                                    key={option.value}
                                                    onClick={() => {
                                                        setJobStatusFilter(option.value);
                                                        setIsDropdownOpen(false);
                                                    }}
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    role="menuitem"
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}


                        <Link
                            to="/patients/create"
                            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                        >
                            <span className="flex items-center gap-2">
                                Add Patient
                                <ChevronRightIcon className="h-4 w-4" />
                            </span>
                        </Link>
                    </div>
                </div>

                {/* Navigation Pills */}
                <div className="mt-6 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('patients')}
                            className={`${activeTab === 'patients'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-all duration-200`}
                        >
                            All Patients
                        </button>
                        <button
                            onClick={() => setActiveTab('jobs')}
                            className={`${activeTab === 'jobs'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-all duration-200`}
                        >
                            Patient Jobs
                        </button>
                    </nav>
                </div>

                {/* Search Bar */}
                <div className="mt-6">
                    <div className="max-w-lg w-full">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder={activeTab === 'patients' ? "Search patients..." : "Search jobs..."}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={activeTab === 'patients' ? patientsSearch : jobsSearch}
                                onChange={e => {
                                    if (activeTab === 'patients') setPatientsSearch(e.target.value);
                                    else setJobsSearch(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                </div>

                {activeTab === 'patients' ? (
                    <>
                        {/* Empty State for Patients */}
                        {!isLoading && patients.length === 0 && (
                            <div className="text-center mt-12">
                                <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No patients found</h3>
                                <p className="mt-1 text-sm text-gray-500">Get started by creating a new patient.</p>
                                <div className="mt-6">
                                    <Link
                                        to="/patients/create"
                                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        <UserIcon className="h-4 w-4 mr-2" />
                                        Add New Patient
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* Patients Grid */}
                        {!isLoading && patients.length > 0 && (
                            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 pb-12">
                                {patients
                                    .filter(patient => {
                                        if (!patientsSearch) return true;
                                        const q = patientsSearch.toLowerCase();
                                        return (
                                            (patient.name && patient.name.toLowerCase().includes(q)) ||
                                            (patient.surname && patient.surname.toLowerCase().includes(q)) ||
                                            (patient.email && patient.email.toLowerCase().includes(q)) ||
                                            (patient.phone && patient.phone.toLowerCase().includes(q)) ||
                                            (patient.location?.city && patient.location.city.toLowerCase().includes(q)) ||
                                            (patient.location?.state && patient.location.state.toLowerCase().includes(q)) ||
                                            (patient.location?.zipCode && patient.location.zipCode.toLowerCase().includes(q))
                                        );
                                    })
                                    .map((patient) => {
                                        return (
                                            <Link
                                                to={`/patients/${patient._id}`}
                                                key={patient._id}
                                                className="group relative flex flex-col rounded-2xl bg-white p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 transition-all duration-300 hover:shadow-xl hover:shadow-sky-900/10 hover:-translate-y-1"
                                            >
                                                <div className="flex items-center">
                                                    <div className="relative flex-shrink-0">
                                                        <img
                                                            className="h-12 w-12 rounded-full object-cover ring-2 ring-white"
                                                            src={patient.avatarUrl || '/default-avatar.png'}
                                                            alt={`${patient.name} ${patient.surname}`}
                                                        />
                                                        <span className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-sky-50 ring-2 ring-white flex items-center justify-center">
                                                            <CheckBadgeIcon className="h-3.5 w-3.5 text-sky-500" />
                                                        </span>
                                                    </div>
                                                    <div className="ml-4">
                                                        <h3 className="text-lg font-semibold text-gray-900">
                                                            {patient.name} {patient.surname}
                                                        </h3>
                                                        <p className="text-sm text-gray-500">{patient.email}</p>
                                                    </div>
                                                </div>

                                                {/* Active Job Badge */}
                                                <div className="mt-4">
                                                    {patient.workingJob ? (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                            {patient.workingJob.title}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                            No active job
                                                        </span>
                                                    )}
                                                </div>

                                                <p className="mt-4 text-sm text-gray-600 line-clamp-2">
                                                    {patient.about || 'No additional info.'}
                                                </p>

                                                <div className="mt-6 space-y-4">
                                                    <div className="flex items-center text-sm text-gray-500">
                                                        <PhoneIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                                                        {patient.phone}
                                                    </div>
                                                    <div className="flex items-center text-sm text-gray-500">
                                                        <MapPinIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                                                        {patient?.location?.city}, {patient?.location?.state}
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* Empty State for Jobs */}
                        {!isLoading && jobs.length === 0 && (
                            <div className="text-center mt-12">
                                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
                                <p className="mt-1 text-sm text-gray-500">There are no patient jobs at the moment.</p>

                            </div>
                        )}

                        {/* Jobs Grid */}
                        {!isLoading && jobs.length > 0 && (
                            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 pb-12">
                                {jobs
                                    .filter(job => jobStatusFilter === 'all' || job.status === jobStatusFilter)
                                    .filter(job => {
                                        if (!jobsSearch) return true;
                                        const q = jobsSearch.toLowerCase();
                                        return (
                                            (job.title && job.title.toLowerCase().includes(q))
                                        );
                                    })
                                    .map((job) => (
                                        <div
                                            key={job._id}
                                            className="group relative flex flex-col rounded-2xl bg-white p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 transition-all duration-300 hover:shadow-xl hover:shadow-sky-900/10 hover:-translate-y-1 min-h-[280px]"
                                        >
                                            {/* Top ribbon for status */}
                                            <div className="absolute top-8 right-5 -translate-y-1/2">
                                                <span className={classNames(
                                                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium shadow-sm',
                                                    job.status === 'working'
                                                        ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 ring-1 ring-green-200'
                                                        : 'bg-gradient-to-r from-blue-100 to-sky-100 text-blue-800 ring-1 ring-blue-200'
                                                )}>
                                                    {job.status}
                                                </span>
                                            </div>

                                            <div className="flex items-start justify-between">
                                                <div className="space-y-1 min-w-0 flex-1">
                                                    <h3 className="text-base font-semibold text-gray-900 line-clamp-2">
                                                        {job.title}
                                                    </h3>
                                                    <Link
                                                        to={`/patients/${job.patient._id}`}
                                                        className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-start max-w-full"
                                                    >
                                                        <UserIcon className="h-3.5 w-3.5 mr-1 flex-shrink-0 mt-0.5" />
                                                        <div className="min-w-0 flex-1">
                                                            <div className="truncate border-b border-blue-300/30">
                                                                {job.patient.name}  {job.patient.surname}
                                                            </div>
                                                        </div>
                                                    </Link>
                                                </div>
                                            </div>

                                            <div className="mt-3 grid grid-cols-[1fr,auto,1fr] items-center gap-3">
                                                <div className="space-y-1.5">
                                                    <div className="flex items-center text-sm text-gray-600">
                                                        <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-sky-50 ring-1 ring-sky-100 flex items-center justify-center mr-2">
                                                            <CalendarIcon className="h-3.5 w-3.5 text-sky-500" />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-medium truncate">{moment(job.date[0]).format('DD MMM, YYYY')}</div>
                                                            {job.date.length > 1 && (
                                                                <div className="text-xs text-gray-400 truncate">
                                                                    to {moment(job?.date[1]).format('DD MMM')}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center text-sm text-gray-600">
                                                        <div className="flex-shrink-0 mt-1 w-7 h-7 rounded-lg bg-sky-50 ring-1 ring-sky-100 flex items-center justify-center mr-2">
                                                            <ClockIcon className="h-3.5 w-3.5 text-sky-500" />
                                                        </div>
                                                        <div className="font-medium">
                                                            {job.time.startTime}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="h-12 w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>

                                                <div className="space-y-1.5">
                                                    <div className="flex items-center text-sm text-gray-600">
                                                        <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-sky-50 ring-1 ring-sky-100 flex items-center justify-center mr-2">
                                                            <UserIcon className="h-3.5 w-3.5 text-sky-500" />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-medium truncate">
                                                                {job.bids[0].provider.name}
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                {job.bids[0].provider.surname}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center text-sm">
                                                        <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-green-50 ring-1 ring-green-100 flex items-center justify-center mr-2">
                                                            <span className="text-green-600 font-semibold">$</span>
                                                        </div>
                                                        <div>
                                                            <span className="font-semibold text-gray-900">${job.price}</span>
                                                            <span className="text-gray-400 text-xs ml-1">USD</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-auto pt-3 border-t border-gray-100">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    {job.status === 'working' && (
                                                        <>
                                                            <button
                                                                disabled={job.status === 'dispute'}
                                                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                                onClick={() => {
                                                                    setModalJob(job);
                                                                    setShowModal('completed');
                                                                }}
                                                            >
                                                                <CheckBadgeIcon className="h-3.5 w-3.5 mr-1" />
                                                                Complete
                                                            </button>

                                                            {moment(
                                                                `${job?.date[0].split('T')[0]}T${job.time.startTime}`
                                                            ).isAfter(moment()) ? (
                                                                <button
                                                                    disabled={job.status === 'dispute'}
                                                                    className="inline-flex items-center px-3 py-1.5 border border-gray-200 text-sm font-medium rounded-md text-gray-700 bg-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    onClick={() => {
                                                                        setShowCancelModal(true);
                                                                        setModalJob(job);
                                                                    }}
                                                                >
                                                                    Cancel job
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    disabled={job.status === 'dispute'}
                                                                    className="inline-flex items-center px-3 py-1.5 border border-gray-200 text-sm font-medium rounded-md text-gray-700 bg-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    onClick={() => {
                                                                        setModalJob(job);
                                                                        setShowModal('refund');
                                                                    }}
                                                                >
                                                                    Request refund
                                                                </button>
                                                            )}
                                                        </>
                                                    )}

                                                    {/* <div className="flex-1"></div> */}

                                                    <Link
                                                        to={`/jobs/${job._id}`}
                                                        className="inline-flex items-center text-sm font-medium text-gray-500"
                                                    >
                                                        View Details
                                                        <ChevronRightIcon className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                                                    </Link>

                                                    {(job.status === 'active' || job.status === 'pending') && (
                                                        <>
                                                            <div className="h-4 w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
                                                            <Link
                                                                to={`/jobs/${job._id}/edit`}
                                                                className="inline-flex items-center text-sm font-medium text-blue-600"
                                                            >
                                                                <PencilSquareIcon className="h-3.5 w-3.5 mr-1" />
                                                                Edit
                                                            </Link>
                                                        </>
                                                    )}

                                                    {job.status === 'completed' && (
                                                        <>
                                                            <div className="h-4 w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
                                                            <button
                                                                className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                                                                onClick={() => {
                                                                    setReviewJob(job);
                                                                    setShowReviewModal(true);
                                                                }}
                                                                disabled={!!job.review}
                                                            >
                                                                <img src={IconReview} alt="Review" className="h-3.5 w-3.5 mr-1" />
                                                                <span>{!!job.review ? 'Reviewed' : 'Add review'}</span>
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Add Completed notification */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="relative w-full h-full">
                    <Completed show={completed} setShow={setCompleted} duration="750" />
                </div>
            </div>

            {/* Add Review Modal */}
            <ReviewModal
                show={showReviewModal}
                onClose={() => setShowReviewModal(false)}
                job={reviewJob}
                onCompleted={() => {
                    setJobs(prev => prev.map(job => {
                        if (job._id === reviewJob._id) {
                            return {
                                ...job,
                                review: true
                            };
                        }
                        return job;
                    }));
                    setCompleted(true);
                    setShowReviewModal(false);
                }}
            />

            {/* Add modals */}
            <div className="fixed inset-0 overflow-y-auto z-50" style={{ display: showModal ? 'block' : 'none' }}>
                <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
                    <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                        <div className="absolute inset-0 bg-gray-500/75 backdrop-blur-sm"></div>
                    </div>

                    <div className="relative transform overflow-hidden rounded-2xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                {showModal === 'completed' ? (
                                    <CheckBadgeIcon className="h-6 w-6 text-blue-600" />
                                ) : (
                                    <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                    </svg>
                                )}
                            </div>
                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                <h3 className="text-lg font-semibold leading-6 text-gray-900">
                                    {showModal === 'completed' ? 'Complete this job?' : 'Request refund for this job?'}
                                </h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">{modalJob?.title}</p>
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button
                                type="button"
                                className={`inline-flex w-full justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${showModal === 'completed'
                                        ? 'bg-blue-600 hover:bg-blue-500 focus-visible:outline-blue-600'
                                        : 'bg-red-600 hover:bg-red-500 focus-visible:outline-red-600'
                                    }`}
                                onClick={showModal === 'completed' ? sendCompleteJob : disputeJob}
                            >
                                {showModal === 'completed' ? 'Yes, complete' : 'Yes, request refund'}
                            </button>
                            <button
                                type="button"
                                className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                onClick={() => setShowModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cancel Modal */}
            <div className="fixed inset-0 overflow-y-auto z-50" style={{ display: showCancelModal ? 'block' : 'none' }}>
                <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
                    <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                        <div className="absolute inset-0 bg-gray-500/75 backdrop-blur-sm"></div>
                    </div>

                    <div className="relative transform overflow-hidden rounded-2xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                            </div>
                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                <h3 className="text-lg font-semibold leading-6 text-gray-900">Cancel this job?</h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">
                                        Are you sure you want to cancel this job? This action cannot be undone.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button
                                type="button"
                                className="inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline-red-600 sm:ml-3 sm:w-auto"
                                onClick={cancelJob}
                            >
                                Yes, cancel job
                            </button>
                            <button
                                type="button"
                                className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                onClick={() => setShowCancelModal(false)}
                            >
                                No, keep job
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PatientsManage;